import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { PROVIDERS } from '@sequesto-os/shared';
import { useIntegrations } from '../../hooks/useIntegrations';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { PageHeader, PageShell, SectionHeading } from '../../components/ui/PageHeader';
import { SegmentedControl } from '../../components/ui/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import {
  COUNT_ORDER,
  HEALTH_LABEL,
  SystemDiagram,
  TEAM_CATEGORIES,
  healthCounts,
  normalizeHealth,
  integrationDisplayName,
  integrationHealth,
  integrationStatus,
  providerTeams,
  useIntegrationExceptions,
  type IntegrationHealth,
  type TeamCategory,
} from '../../components/SystemDiagram';
import { AvailableRow, IntegrationListHead, IntegrationRow, sharedSyncAge, syncedText } from './IntegrationRow';
import { ConfigDrawer } from './ConfigDrawer';
import type { Integration } from '../../api/integrations';

const AVAILABLE_PROVIDERS = Object.entries(PROVIDERS)
  .filter(([, def]) => !def.hidden)
  .map(([id, def]) => ({
    id,
    name: def.name,
    description: def.description,
    category: def.category,
    usage: def.usage,
    tags: def.tags,
    multiInstance: def.multiInstance ?? false,
  }));

/** The same team set the diagram groups by, so a filter and an arc always match. */
const TEAM_FILTERS: { id: TeamCategory | 'all'; label: string }[] = [{ id: 'all', label: 'All' }, ...TEAM_CATEGORIES];

/** The lists under "Set up": each heading is true of every row under it. Labels match the legend. */
const SET_UP_GROUPS: { id: string; label: string; states: IntegrationHealth[] }[] = [
  { id: 'attention', label: 'Needs a look', states: ['failing', 'stuck', 'pending'] },
  { id: 'connected', label: 'Connected', states: ['ok', 'syncing'] },
  { id: 'key_set', label: 'Key set, no use seen', states: ['key_set'] },
  { id: 'disabled', label: 'Disabled', states: ['disabled', 'not_connected'] },
];

/** Problems first, then anything not plainly healthy, then alphabetical. */
const HEALTH_ORDER: Record<IntegrationHealth, number> = {
  failing: 0,
  stuck: 1,
  pending: 2,
  syncing: 3,
  disabled: 4,
  not_connected: 5,
  ok: 6,
  key_set: 7,
};

/**
 * Status warnings (the API could not read something) above the diagram. What is
 * failing or stuck is listed once, under "Needs a look" below; repeating it in a
 * banner here only said it a second time. Hidden when there are no warnings.
 */
function StatusWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-1.5 rounded-2xl border border-border-light bg-surface-primary px-4 py-3">
      {warnings.map((w) => (
        <p key={w} className="flex items-center gap-2 text-body-xs text-text-secondary">
          <span className="h-1.5 w-1.5 flex-none rounded-full bg-warning" aria-hidden />
          {w}
        </p>
      ))}
    </div>
  );
}

export function Integrations() {
  const { data, isLoading, error, refetch } = useIntegrations();
  const { warnings, data: payload } = useIntegrationExceptions();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tagFilter, setTagFilter] = useState<TeamCategory | 'all'>('all');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showNotConnected, setShowNotConnected] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newProvider, setNewProvider] = useState<string | null>(null);

  const handleConfigure = (integration: Integration) => {
    if (!isAdmin) return;
    setSelectedIntegration(integration);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAddNew = (providerId: string | null) => {
    setNewProvider(providerId);
    setSelectedIntegration(null);
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
    setSelectedIntegration(null);
    setNewProvider(null);
    setIsCreating(false);
    refetch();
  };

  const integrations = useMemo(() => data?.integrations ?? [], [data]);

  // The list keeps the height it has under "All" while a team filter is on,
  // so the page below it does not jump up and down as filters change.
  const listRef = useRef<HTMLDivElement>(null);
  const [allHeight, setAllHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (tagFilter === 'all' && listRef.current) setAllHeight(listRef.current.offsetHeight);
  });

  const matchesTag = (provider: string) => tagFilter === 'all' || providerTeams(provider).includes(tagFilter);

  const connected = useMemo(
    () =>
      integrations
        .filter((i) => matchesTag(i.provider))
        .sort(
          (a, b) =>
            HEALTH_ORDER[integrationHealth(a)] - HEALTH_ORDER[integrationHealth(b)] ||
            integrationDisplayName(a).localeCompare(integrationDisplayName(b))
        ),
    [integrations, tagFilter]
  );

  const configuredProviders = new Set(integrations.map((i) => i.provider));
  const availableProviders = AVAILABLE_PROVIDERS.filter((p) => p.multiInstance || !configuredProviders.has(p.id));
  // The not-yet-connected list (formerly the Usage tab) shows only single-instance
  // providers nobody has set up; multi-instance ones are added via "New integration".
  // A key in the secret store (or env) with no connection row is configured:
  // it is listed with the connected ones as "Key set", not as not connected.
  const unconnectedHealth = new Map((payload?.unconnected ?? []).map((u) => [u.provider, normalizeHealth(u.health)]));
  const keyOnlyAll = AVAILABLE_PROVIDERS.filter(
    (p) => !configuredProviders.has(p.id) && unconnectedHealth.get(p.id)?.configuredVia,
  );
  const keyOnly = keyOnlyAll.filter((p) => matchesTag(p.id));
  const notConnected = AVAILABLE_PROVIDERS.filter(
    (p) => !configuredProviders.has(p.id) && !unconnectedHealth.get(p.id)?.configuredVia && matchesTag(p.id),
  );
  // "Key set, no use seen": a list where no row has a use on record would repeat one word down the column.
  const keySetHasActivity =
    connected.some((i) => integrationHealth(i) === 'key_set' && (!!integrationStatus(i).lastActivity?.at || integrationStatus(i).hasSync)) ||
    keyOnly.some((p) => {
      const h = unconnectedHealth.get(p.id);
      return (h?.state ?? 'key_set') === 'key_set' && !!h?.lastActivity?.at;
    });
  // A sync time most rows of a list share is said once in that list's heading, not on every row.
  const sharedByGroup = new Map(
    SET_UP_GROUPS.map((g) => [
      g.id,
      sharedSyncAge(connected.filter((i) => g.states.includes(integrationHealth(i))).map((i) => syncedText(integrationStatus(i)))),
    ]),
  );
  // Connection rows and key-only tools (DeepL) together, sorted by state then name.
  const setUpRows = [
    ...connected.map((integration) => ({
      key: integration.id,
      name: integrationDisplayName(integration),
      state: integrationHealth(integration),
      node: (
        <IntegrationRow
          key={integration.id}
          sharedSync={sharedByGroup.get(SET_UP_GROUPS.find((g) => g.states.includes(integrationHealth(integration)))?.id ?? '') ?? null}
          integration={integration}
          onConfigure={() => handleConfigure(integration)}
          isAdmin={isAdmin}
          showActivity={integrationHealth(integration) !== 'key_set' || keySetHasActivity}
        />
      ),
    })),
    ...keyOnly.map((p) => ({
      key: p.id,
      name: p.name,
      state: unconnectedHealth.get(p.id)?.state ?? ('key_set' as IntegrationHealth),
      node: (
        <AvailableRow
          key={p.id}
          def={p}
          keySource={unconnectedHealth.get(p.id)?.configuredVia}
          health={unconnectedHealth.get(p.id)}
          showActivity={(unconnectedHealth.get(p.id)?.state ?? 'key_set') !== 'key_set' || keySetHasActivity}
          onAdd={isAdmin ? () => handleAddNew(p.id) : undefined}
        />
      ),
    })),
  ].sort((a, b) => HEALTH_ORDER[a.state] - HEALTH_ORDER[b.state] || a.name.localeCompare(b.name));

  // The header counts the same categories as the legend under the diagram, and they add up.
  const summary = healthCounts(payload);
  const summaryParts = COUNT_ORDER.filter((h) => summary.counts[h] > 0).map(
    (h) => `${summary.counts[h]} ${HEALTH_LABEL[h].charAt(0).toLowerCase()}${HEALTH_LABEL[h].slice(1)}`,
  );

  return (
    <PageShell>
      <PageHeader eyebrow="Admin"
        title="Integrations"
        subtitle={isAdmin ? 'Connect and manage your external services.' : 'Connected external services and their status.'}
        meta={
          summary.total > 0 ? (
            <span>
              {summary.total} set up · {summaryParts.join(' · ')}
            </span>
          ) : undefined
        }
        actions={
          isAdmin && availableProviders.length > 0 ? (
            <Button variant="primary" onClick={() => handleAddNew(null)} leftIcon={<Plus className="h-4 w-4" />}>
              New integration
            </Button>
          ) : undefined
        }
      />

      <StatusWarnings warnings={warnings} />

      <SystemDiagram onSelect={isAdmin ? handleConfigure : undefined} />

      {isLoading ? (
        <LoadingState rows={6} />
      ) : error ? (
        <ErrorState title="Couldn’t load integrations" error={error} onRetry={() => refetch()} />
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            <SectionHeading
              action={
                <SegmentedControl options={TEAM_FILTERS} value={tagFilter} onChange={setTagFilter} ariaLabel="Team" />
              }
            >
              Set up
            </SectionHeading>
            <div ref={listRef} className="space-y-4" style={tagFilter !== 'all' && allHeight ? { minHeight: allHeight } : undefined}>
            {setUpRows.length === 0 ? (
              <EmptyState
                compact
                title={tagFilter === 'all' ? 'No integrations configured' : 'No integrations set up for this team'}
                description={!isAdmin && tagFilter === 'all' ? 'Ask an administrator to configure integrations.' : undefined}
              />
            ) : (
              // One list per state, each under a heading that is true of every row in it.
              SET_UP_GROUPS.map((g) => {
                const rows = setUpRows.filter((r) => g.states.includes(r.state));
                if (rows.length === 0) return null;
                const showActivity = g.id !== 'key_set' || keySetHasActivity;
                return (
                  <div key={g.id} className="space-y-1.5">
                    <p className="eyebrow text-text-tertiary">
                      {g.label} <span className="tabular-nums">{rows.length}</span>
                      {!showActivity && <span className="font-normal normal-case"> · last use not recorded yet, the OS is collecting usage evidence</span>}
                      {showActivity && sharedByGroup.get(g.id) && (
                        <span className="font-normal normal-case"> · most {sharedByGroup.get(g.id)!.replace(/^Synced/, 'synced')}, shown only where it differs</span>
                      )}
                    </p>
                    <div className="divide-y divide-border-light rounded-2xl border border-border-light bg-surface-primary">
                      <IntegrationListHead showStatus={g.id === 'attention'} showActivity={showActivity} />
                      {rows.map((r) => r.node)}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </div>

          {notConnected.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowNotConnected((v) => !v)}
                aria-expanded={showNotConnected}
                className="group flex items-center gap-1"
              >
                <SectionHeading>Not connected</SectionHeading>
                <span className="text-body-xs tabular-nums text-text-tertiary">{notConnected.length}</span>
                {showNotConnected ? (
                  <ChevronDown className="h-3.5 w-3.5 text-text-muted group-hover:text-text-primary" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-text-muted group-hover:text-text-primary" aria-hidden />
                )}
              </button>
              {showNotConnected && <div className="divide-y divide-border-light rounded-2xl border border-dashed border-border-light bg-surface-primary">
                {notConnected.map((p) => (
                  <AvailableRow
                    key={p.id}
                    def={p}
                    note={unconnectedHealth.get(p.id)?.reason ?? undefined}
                    noteSource={unconnectedHealth.get(p.id)?.source}
                    onAdd={isAdmin ? () => handleAddNew(p.id) : undefined}
                  />
                ))}
              </div>}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <ConfigDrawer
          isOpen={isDrawerOpen}
          onClose={handleClose}
          integration={selectedIntegration}
          providerId={newProvider}
          isCreating={isCreating}
          availableProviders={availableProviders}
        />
      )}
    </PageShell>
  );
}
